import "./style.css";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AutoComplete, OptionList, Switcher, Title, PopUpModal } from "../../Components/Atoms";
import { jsonToCSVConvertor } from "../../Library/jsonUtils";
import { DownloadIcon, InfoFilledIcon, ResetIcon, UploadOutlinedIcon, UploadingIcon } from "../../Assets/images/svgs";
import { Button, Input, Popover, PopoverArrow, PopoverBody, PopoverCloseButton, PopoverContent, PopoverTrigger, Spinner, useToast } from "@chakra-ui/react";
import { useDebounce } from "../../Hooks";
import { getAllDealers, getDeviceSubscriptionList, submitRenewalRequest, getRenewalHistory } from "../../APIs";
import { ReactTable } from "../../Components/Molecules";
import { SUBSCTIPION_DEVS_HEADERS, RENEWAL_HISTORY_HEADERS } from "../../Library/tableHeaders";
import PATHS from "../../Routes/paths";
import { getLocalisedDate } from "../../Library/datetime";
import { StoreContext } from "../../Library/contexts";

const POPOVER_TEXT =
  "Device has been inactive for over 30 days after expiry. SIM is now inactive and needs replacement.";

const switcherOptions = [
  { idx: 1, label: "Summary" },
  { idx: 2, label: "Renewal History" }
];

const StatusPopover = ({ text, onOpen, onClose }) => (
  <Popover placement="bottom" onOpen={onOpen} onClose={onClose}>
    <PopoverTrigger>
      <InfoFilledIcon className="status-popover-trigger" />
    </PopoverTrigger>

    <PopoverContent width="400px" className="sim-inactive-popover">
      <PopoverArrow className="sim-inactive-popover-close" />
      <PopoverCloseButton />
      <PopoverBody>{text}</PopoverBody>
    </PopoverContent>
  </Popover>
);



export default function DeviceSubscription() {
  const navigate = useNavigate();
  const toast = useToast();
  const { allDealers, setAllDealers, subsDevs, setSubsDevs, subsRenewals, setSubsRenewals } = useContext(StoreContext);

  const [searchParams, setSearchParams] = useSearchParams();
  const switcherVal = parseInt(searchParams.get("tab") || "1");
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchValue, handleSearch] = useDebounce((val) => setDebouncedSearch(val), 500);
  const fetchedDevs = useRef(false);
  const fetchedRenewals = useRef(false);
  const noRenewalsFound = useRef(false)

  const [dealer, setDealer] = useState({});
  const [selectedCard, setSelectedCard] = useState(null); // Track which card is selected (0: Active, 1: Expiring, 2: Expired, 3: SIM Inactive)

  const [filters, setFilters] = useState({ days: { value: 1, label: "All Days" }, renewal: {}, status: {} });

  const filterConfig = useMemo(() => {
    const isExpiryFilterActive = filters.days.value === 2 || filters.days.value === 3;
    const isStatusFilterActive = filters.status.value === 3 || filters.status.value === 4;

    return [
      {
        key: "days",
        label: "Next 30 Days",
        options: [
          { label: "All Days", value: 1 },
          { label: "Next 0 - 30 Days", value: 2 },
          { label: "Next 31 - 60 Days", value: 3 }
        ]
      },
      {
        key: "renewal",
        label: "All Renewals",
        options: [
          { label: "All Renewals", value: 1 },
          { label: "Pending Renewals", value: 2 },
          { label: "Renewal Requested", value: 3 }
        ]
      },
      // {
      //   key: "status",
      //   label: "All Status",
      //   options: [
      //     { label: "All Status", value: 1 },
      //     { label: "Active", value: 2 },
      //     { label: "Subscription Expired", value: 3, disabled: isExpiryFilterActive },
      //     { label: "SIM Blocked", value: 4, disabled: isExpiryFilterActive }
      //   ]
      // }
    ];
  }, [filters.days.value, filters.status.value]);

  // Data States
  const [devs, setDevs] = useState([]);
  const [allDevs, setAllDevs] = useState([]); // Cache for client-side search
  const [devStats, setDevStats] = useState({ active: 0, expiring: 0, expired: 0, simInactive: 0, total: 0 });
  const [renewalHistoryData, setRenewalHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDevices, setTotalDevices] = useState(0);
  const [fetchedPages, setFetchedPages] = useState(new Set([1])); // Track which backend pages we've fetched
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(false);
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  const lastQueryRef = useRef({ switcherVal, filters, dealer, debouncedSearch });

  // Renewal History States
  const [invoiceFilter, setInvoiceFilter] = useState({});
  const [imeiReportFilter, setImeiReportFilter] = useState({});
  const [selectedRenewalRows, setSelectedRenewalRows] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [isGlobalSelectActive, setIsGlobalSelectActive] = useState(false);
  const isGlobalSelectRef = useRef(false); // Ref to track global intent inside stale closures (async fetch)
  const [popupModalData, setPopupModalData] = useState(null);
  const [pendingDownloadOption, setPendingDownloadOption] = useState(null);
  const [pendingRenewal, setPendingRenewal] = useState(false);

  // Seed device list from context cache, if available (e.g. on remount)
  useEffect(() => {
    if (!devs.length && subsDevs && subsDevs.length) {
      setDevs(subsDevs);
      setAllDevs(subsDevs);
    }
  }, [subsDevs, devs.length]);

  // Seed renewal history from context cache, if available (e.g. on remount)
  useEffect(() => {
    if (!renewalHistoryData.length && subsRenewals && subsRenewals.length) {
      setRenewalHistoryData(subsRenewals);
    }
  }, [subsRenewals, renewalHistoryData.length]);

  useEffect(() => {
    if (allDealers.length === 0) {
      getAllDealers(null, (err, dealers) => {
        if (err) console.error(err);
        else
          setAllDealers([
            { label: "All Dealerships", value: "" },
            ...dealers.map((d) => ({ label: d.nm, value: d.idx }))
          ]);
      });
    }
  }, []);

  /* --- Client Side Filtering Logic Helpers --- */
  const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
  const NINETY_DAYS = 90 * 24 * 60 * 60 * 1000;

  const calculateDeviceStatus = (sed) => {
    const now = Date.now();
    if (!sed) return 3; // Default Expired
    if (sed > now + THIRTY_DAYS) return 1; // Active
    if (sed > now) return 2; // Expiring
    if (sed > now - NINETY_DAYS) return 3; // Expired
    return 4; // SIM Inactive
  };

  const calculateStats = (devices) => {
    const stats = { active: 0, expiring: 0, expired: 0, simInactive: 0, total: devices.length };
    devices.forEach(d => {
      // Use existing status if available, else recalc
      const st = d.status || calculateDeviceStatus(d.sed);
      if (st === 1) stats.active++;
      else if (st === 2) stats.expiring++;
      else if (st === 3) stats.expired++;
      else if (st === 4) stats.simInactive++;
    });
    return stats;
  };

  const fetchAllDevicesInBackground = useCallback((initialParams, totalCount) => {
    setIsBackgroundLoading(true);
    const params = {
      ...initialParams,
      page: 1,
      limit: totalCount
    };

    // Parallel fetch: Devices + Renewal History (Decoupled for faster UI updates)

    // 1. Fetch Renewal History immediately (independent of large device list)
    if (!subsRenewals || !subsRenewals.length) {
       getRenewalHistory({ page: 1, limit: 10000 })
         .then((res) => {
             const mappedData = (res.data || []).map(item => ({
              idx: item.idx,
              reqId: `R${String(item.idx).padStart(3, '0')}`,
              reqDate: new Date(item.createdAt).toLocaleDateString(),
              updatedAt: item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : '-',
              devices: item.deviceCount,
              status: getStatusLabel(item.status),
              statusId: item.status,
              rawDevices: item.devices || []
           }));
           setRenewalHistoryData(mappedData);
           setSubsRenewals(mappedData);
         })
         .catch(err => console.error("Error fetching background renewal history:", err));
    }

    // 2. Fetch Devices
    getDeviceSubscriptionList(params)
      .then((devRes) => {
        const mappedDevs = (devRes.data || []).map(d => {
          const dealerObj = allDealers.find(ad => ad.value === d.dlrId);
          return {
            ...d,
            dlr: dealerObj ? dealerObj.label : (d.dlr || "-"),
            imei: d.imei || "-"
          };
        });

        // Update Dev Stats
        const stats = calculateStats(mappedDevs);
        setDevStats(stats);

        setDevs(mappedDevs);
        setAllDevs(mappedDevs);
        setSubsDevs(mappedDevs);
        setTotalDevices(mappedDevs.length);
        setAllDataLoaded(true);
        setIsBackgroundLoading(false);

        // If Global Select was active, update selection to include NEWLY loaded devices
        if (isGlobalSelectRef.current) {
            const allIds = mappedDevs.map(d => d.idx);
            setSelectedDevices(allIds);
        }
      })
      .catch((err) => {
        console.error("Error fetching background devices:", err);
        setIsBackgroundLoading(false);
      });
  }, [allDealers, setSubsDevs, subsRenewals, setSubsRenewals]);

  const applyClientFilters = (devices) => {
      let filtered = devices;

      // 1. Search
      if (debouncedSearch) {
         const lowerSearch = debouncedSearch.toLowerCase();
         filtered = filtered.filter(d =>
          (d.reg && d.reg.toLowerCase().includes(lowerSearch)) ||
          (d.imei && String(d.imei).toLowerCase().includes(lowerSearch)) ||
          (d.chNum && d.chNum.toLowerCase().includes(lowerSearch)) ||
          (d.dlr && d.dlr.toLowerCase().includes(lowerSearch))
        );
      }

      // 2. Dealer
      if (Number(dealer)) {
          filtered = filtered.filter(d => +d.dlrId === +dealer);
      }

      // 3. Status
      const stVal = filters.status.value;
      if (stVal && stVal !== 1) {
          // 2: Active(1,2), 3: Inactive(3,4), 4: SimBlocked(4) - mapping from backend
          // Backend Logic:
          // statusFilter 2 (Active Tab?): {status: {$in: [1, 2]}} -> Active + Expiring
          // statusFilter 3 (Expired Tab?): {status: {$in: [3, 4]}} -> Expired + Inactive
          // statusFilter 4 (Sim Blocked): {status: 4} -> Inactive

          // FrontEnd Dropdown: Active (2), Expired(3), Blocked(4)
          // Mapped to:
          if (stVal === 2) filtered = filtered.filter(d => d.status === 1 || d.status === 2);
          else if (stVal === 3) filtered = filtered.filter(d => d.status === 3 || d.status === 4);
          else if (stVal === 4) filtered = filtered.filter(d => d.status === 4);
      }

      // 4. Expiry (Days)
      const expVal = filters.days.value;
      if (expVal) {
          const now = Date.now();
          // 2: Next 0-30 Days. Backend: status=2
          if (expVal === 2) {
              filtered = filtered.filter(d => d.status === 2);
          }
          // 3: Next 31-60 Days. Backend: status=1 AND daysToExpiry 30-60
          // Helper for days
          else if (expVal === 3) {
              filtered = filtered.filter(d => {
                  const daysToExpiry = Math.ceil(((d.sed || 0) - now)/(24*60*60*1000));
                  return d.status === 1 && daysToExpiry > 30 && daysToExpiry <= 60;
              });
          }
      }

      // 5. Renewal
      const renVal = filters.renewal.value;
      if (renVal && renVal > 1) {
          // Need to check against subsRenewals
          // Build set of IMEIs/Regs from subsRenewals (Status 1,2,3 -> Requested/PI/Payment)
          // Backend Renewal Query: status IN [1,2,3]
          const activeRenewals = (subsRenewals || []).filter(r => [1, 2, 3].includes(r.statusId));
          const requestedImeis = new Set();

          activeRenewals.forEach(req => {
             (req.rawDevices || []).forEach(rd => {
                 if (rd.imei) requestedImeis.add(String(rd.imei));
                 if (rd.regNo) requestedImeis.add(rd.regNo);
             });
          });

          if (renVal === 2) { // Pending (Not Requested)
              filtered = filtered.filter(d => !requestedImeis.has(String(d.imei)) && !requestedImeis.has(d.reg));
          } else if (renVal === 3) { // Requested
              filtered = filtered.filter(d => requestedImeis.has(String(d.imei)) || requestedImeis.has(d.reg));
          }
      }

      return filtered;
  };

  /* --- Effects --- */

  // Re-run filter/fetch when full data is loaded (to apply pending filters OR pending download)
  useEffect(() => {
    if (allDataLoaded) {
       fetchDevices(1, false);

       // Handle pending download if queued
       if (pendingDownloadOption) {
          handleSubDevsDownload(pendingDownloadOption);
          setPendingDownloadOption(null);
          setLoading(false);
       }

       // Handle pending renewal request if queued
       if (pendingRenewal) {
         setPendingRenewal(false);
         setLoading(false);
         // Timeout to allow state updates to settle if needed, or just call directly
         // handleRenewalRequest relies on selectedDevices, which is updated BEFORE this effect via fetchAll ref logic.
         // However, handleRenewalRequest might check isBackgroundLoading? No, current check is inside.
         // We need to bypass the check or ensure isBackgroundLoading is false (it is).
         setTimeout(() => handleRenewalRequest(), 0);
       }
    }
  }, [allDataLoaded]);

  const fetchDevices = (page = 1, append = false) => {
    fetchedDevs.current = true;

    // User Request: If background loading is in progress and filter changes, 
    // show loader and wait for background load to finish (blocking API call).
    if (isBackgroundLoading) {
        setLoading(true);
        return;
    }

    const hasActiveFilters = filters.days.value || filters.status.value || filters.renewal.value || dealer; // dealer is value directly now per fix

    // Disable cache optimization when pagination is active
    // Empty search optimization (ONLY if no filters are active and on page 1)
    if (!debouncedSearch && !hasActiveFilters && allDevs.length > 0 && page === 1 && !append) {
      setDevs(allDevs);
      setTotalDevices(allDevs.length);
      // If we have cached data, check if it seems complete (comparison with stats or just assume)
      // For now, if pulling from context cache, we assume standard behavior.
      // If we want to ensure background load runs again if needed:
       if (allDevs.length > 20 && !allDataLoaded) setAllDataLoaded(true);
      return;
    }

    // Client-side search optimization (ONLY if no filters are active and on page 1)
    if (debouncedSearch && !hasActiveFilters && allDevs.length > 0 && page === 1 && !append) {
      const lowerSearch = debouncedSearch.toLowerCase();
      const localMatches = allDevs.filter(d =>
        (d.reg && d.reg.toLowerCase().includes(lowerSearch)) ||
        (d.imei && String(d.imei).toLowerCase().includes(lowerSearch)) ||
        (d.chNum && d.chNum.toLowerCase().includes(lowerSearch)) ||
        (d.dlr && d.dlr.toLowerCase().includes(lowerSearch))
      );
      setDevs(localMatches);
      setTotalDevices(localMatches.length);
      return;
    }

    // If all data is loaded, do client-side filtering instead of backend call
    if (allDataLoaded && !append) {
       const filtered = applyClientFilters(allDevs);
       setDevs(filtered);
       setTotalDevices(filtered.length);
       setLoading(false); // Fix: Turn off loader
       return;
    }

    setLoading(true);
    const params = {
      page: page,
      limit: 20, // Backend limit
      search: debouncedSearch,
      expiry: filters.days.value,
      status: filters.status.value,
      renewal: filters.renewal.value,
      dlrId: dealer.value || (dealer && typeof dealer !== 'object' ? dealer : undefined)
    };

    // Remove undefined, null, or empty string values
    Object.keys(params).forEach(key => {
      if (params[key] === undefined || params[key] === null || params[key] === "") {
        delete params[key];
      }
    });

    getDeviceSubscriptionList(params)
      .then((res) => {
        const mappedDevs = (res.data || []).map(d => {
          // Ensure type safety for comparison
          const dealerObj = allDealers.find(ad => ad.value === d.dlrId);
          return {
            ...d,
            dlr: dealerObj ? dealerObj.label : (d.dlr || "-"), // Fallback to existing dlr if available
            imei: d.imei || "-"
          };
        });

        if (append) {
          setDevs(prev => { return [...prev, ...mappedDevs]; });
          setFetchedPages(prev => new Set([...prev, page]));
        } else {
          setDevs(mappedDevs);
          setFetchedPages(new Set([page])); // Reset fetched pages for a new non-append fetch
        }

        // Update cache if not searching and on page 1
        if (!debouncedSearch && page === 1) {
          setAllDevs(mappedDevs);
        }

        // Store latest devices list in context for reuse when revisiting the page
        if (!append && page === 1) {
          setSubsDevs(mappedDevs);
        }

        setTotalDevices(res.total || 0);
        setCurrentPage(page);
        if (res.stats) {
          setDevStats(res.stats);
        }
        setLoading(false);

        // Trigger background load if:
        // 1. We are on page 1
        // 2. No search/complex filters (default view)
        // 3. Total items > 20
        // 4. Not already loaded
        // 5. Not already loading
        if (page === 1 && !debouncedSearch && !append && (res.total || 0) > 20 && !allDataLoaded && !isBackgroundLoading) {
           fetchAllDevicesInBackground(params, res.total || 0);
        }
      })
      .catch((err) => {
        console.error("Error fetching devices:", err);
        setLoading(false);
      });
  };

  const fetchRenewalHistory = () => {
    fetchedRenewals.current = true;
    setLoading(true);
    getRenewalHistory({ page: 1, limit: 100 })
      .then((res) => {
        // Map backend response to table format
        const mappedData = (res.data || []).map(item => ({
          idx: item.idx, // Use idx as key
          reqId: `R${String(item.idx).padStart(3, '0')}`, // Format as R001
          reqDate: new Date(item.createdAt).toLocaleDateString(),
          updatedAt: item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : '-',
          devices: item.deviceCount,
          status: getStatusLabel(item.status),
          statusId: item.status,
          rawDevices: item.devices || []
        }));
        setRenewalHistoryData(mappedData);

        // Store in context so we can reuse on tab switch / remount
        setSubsRenewals(mappedData);
        noRenewalsFound.current = mappedData.length === 0;

        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching renewal history:", err);
        setLoading(false);
      });
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 1: return "Renewal Requested";
      case 2: return "PI Raised";
      case 3: return "Payment Done";
      case 4: return "Renewed";
      default: return "Unknown";
    }
  };

  useEffect(() => {
    if (!allDealers || !allDealers.length) return;

    const prev = lastQueryRef.current;
    const filtersChanged =
      JSON.stringify(prev.filters) !== JSON.stringify(filters) ||
      JSON.stringify(prev.dealer || {}) !== JSON.stringify(dealer || {}) ||
      prev.debouncedSearch !== debouncedSearch;

    // update ref for next run
    lastQueryRef.current = { switcherVal, filters, dealer, debouncedSearch };

    // If query (filters/search/dealer) didn't change and we already have devices,
    // avoid refetching on Summary tab (covers both tab-switch and post-fetch re-run).
    if (!filtersChanged && switcherVal === 1 && devs.length) {
      return;
    }

    setCurrentPage(1); // Reset to page 1 when filters change
    setFetchedPages(new Set([1])); // Reset fetched pages tracking
    // setAllDataLoaded(false); // OPTIMIZATION: Do NOT reset loaded state, try client filter first.

    if (switcherVal === 1) {
      fetchDevices(1, false);
    } else {
      if (renewalHistoryData.length || (subsRenewals && subsRenewals.length) || noRenewalsFound.current) {
        return;
      }
      fetchRenewalHistory();
    }
  }, [switcherVal, filters, debouncedSearch, dealer, allDealers, renewalHistoryData.length, subsRenewals, devs.length]);

  // Update dealer names in existing data when allDealers is loaded
  useEffect(() => {
    if (allDealers.length) {
      const mapDealers = (list) => list.map(d => {
        const dealerObj = allDealers.find(ad => String(ad.value) === String(d.dlrId));
        return {
          ...d,
          dlr: dealerObj ? dealerObj.label : (d.dlr || "-")
        };
      });

      setDevs(prev => mapDealers(prev));
      setAllDevs(prev => mapDealers(prev));
    }
  }, [allDealers]);

  const updateFilter = (key, value) => {
    setFilters((prev) => {
      if (prev[key] && prev[key].value === value.value) return prev; // Avoid redundant updates

      // Special case: If "Expiring in next 30 days" card is selected and user manually changes "All Days" dropdown
      if (key === "days" && selectedCard === 1) {
        setSelectedCard(null); // Reset card selection
      }

      return { ...prev, [key]: value };
    });
  };

  const handleCardClick = (cardIndex) => {
    // If clicking the same card, reset days and status filters (preserve renewal)
    if (selectedCard === cardIndex) {
      setSelectedCard(null);
      setFilters(prev => ({
        ...prev,
        days: { value: 1, label: "All Days" },
        status: {}
      }));
      return;
    }

    // Set the new selected card
    setSelectedCard(cardIndex);

    // Apply appropriate filter based on card (preserve renewal filter)
    setFilters(prev => {
      switch (cardIndex) {
        case 0: // Active Devices
          return {
            ...prev,
            days: { value: 1, label: "All Days" },
            status: { value: 2, label: "Active" }
          };
        case 1: // Expiring in next 30 days
          return {
            ...prev,
            days: { value: 2, label: "Next 0 - 30 Days" },
            status: {}
          };
        case 2: // Subscription Expired
          return {
            ...prev,
            days: { value: 1, label: "All Days" },
            status: { value: 3, label: "Subscription Expired" }
          };
        case 3: // SIM Inactive
          return {
            ...prev,
            days: { value: 1, label: "All Days" },
            status: { value: 4, label: "SIM Blocked" }
          };
        default:
          return prev;
      }
    });
  };

  const handlePageChange = useCallback((newPageIndex) => {
    const nextPage = newPageIndex + 1; // react-table uses 0-indexed pages
    const itemsPerPage = 10; // Frontend table shows 10 items per page
    const currentItemCount = devs.length;
    const requiredItems = nextPage * itemsPerPage;

    // Check if we need to fetch more data from backend
    if (allDataLoaded) return; // No need to fetch if all data is loaded

    if (requiredItems > currentItemCount && currentItemCount < totalDevices) {
      // Calculate which backend page we need based on required items
      // Backend returns 20 items per page, so page 1 = items 1-20, page 2 = items 21-40, etc.
      const backendPageNeeded = Math.ceil(requiredItems / 20);

      // Only fetch if we haven't already fetched this page
      if (!fetchedPages.has(backendPageNeeded)) {
        fetchDevices(backendPageNeeded, true);
      }
    }
  }, [devs.length, totalDevices, fetchedPages]);

  const devStatuses = [
    {
      label: "Active Devices",
      count: devStats.active,
      bgStart: "var(--ActiveDevCardBgGradStart)",
      bgEnd: "var(--ActiveDevCardBgGradEnd)",
      shadow: "0px 3px 10px 0px color-mix(in srgb, var(--ActiveDevCardBgGradEnd) 30%, transparent)"
    },
    {
      label: "Expiring in next 30 days",
      count: devStats.expiring,
      bgStart: "var(--ExpiringDevCardBgGradStart)",
      bgEnd: "var(--ExpiringDevCardBgGradEnd)",
      shadow: "0px 3px 10px 0px color-mix(in srgb, var(--ExpiringDevCardBgGradEnd) 30%, transparent)"
    },
    {
      label: "Subscription Expired",
      count: devStats.expired,
      bgStart: "var(--ExpiredDevCardBgGradStart)",
      bgEnd: "var(--ExpiredDevCardBgGradEnd)",
      shadow: "0px 3px 10px 0px color-mix(in srgb, var(--ExpiredDevCardBgGradEnd) 30%, transparent)"
    },
    {
      label: "SIM Inactive",
      count: devStats.simInactive || 0,
      bgStart: "var(--SimInactiveCardBgGradStart)",
      bgEnd: "var(--SimInactiveCardBgGradEnd)",
      shadow: "0px 3px 10px 0px color-mix(in srgb, var(--SimInactiveCardBgGradEnd) 30%, transparent)",
      popover: POPOVER_TEXT
    }
  ];

  const columns = useMemo(() => {
    const cols = [];
    Object.values(SUBSCTIPION_DEVS_HEADERS).forEach((header) => {
      cols.push({
        Header: header.nm,
        accessor: header.key,
        disableSortBy: !header.sorting,
        disableFilters: !header.showSearch,
        Cell: ({ row, value, column }) => {
          if (column.Header === "Expiring Date") {
            return value ? getLocalisedDate(value, true) : "-";
          }
          if (column.Header === "Expiring In") {
            let sed = row.original.sed;
            if (!sed) return "-";
            sed = parseInt(sed) || 0; // Parse sed as integer
            const now = Date.now();
            const diffTime = sed - now;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            if (diffDays < 0) return "Expired";
            return `${diffDays} Days`;
          }
          return value || "-";
        }
      });
    });

    return cols;
  }, []);

  const renewalColumns = useMemo(() => {
    const cols = [];
    Object.values(RENEWAL_HISTORY_HEADERS).forEach((header) => {
      cols.push({
        Header: header.nm,
        accessor: header.key,
        Cell: ({ row, value, column }) => {
          if (column.id === "reqId") {
            return (
              <p className="req-id-text">
                <Link to={`${PATHS.DEVICE_SUBSCRIPTION}${PATHS.RENEWAL_DETAIL}?reqId=${row.original.idx}`}>
                  {value}
                </Link>
              </p>
            );
          }
          return value;
        }
      });
    });
    return cols;
  }, []);

  const handleRowSelect = (allSelected, selectedRows = []) => {
    // console.log("handleRowSelect", allSelected, selectedRows.length, allDataLoaded);
    const selectedIds = (selectedRows || []).map(row => row.original.idx);
    setSelectedDevices(selectedIds);

    // If user clicked "Select All" (allSelected=true) and we are currently loading background data (!allDataLoaded),
    // we assume they want to select ALL devices (GLOBAL SELECT).
    if (allSelected && !allDataLoaded) {
        setIsGlobalSelectActive(true);
        isGlobalSelectRef.current = true;
    } else if (!allSelected) {
        // If unchecking, disable global select
        setIsGlobalSelectActive(false);
        isGlobalSelectRef.current = false;
    }
  };

  const handleRenewalRowSelect = (allSelected, selectedRows = []) => {
    const selectedIds = (selectedRows || []).map(row => row.original.idx);
    setSelectedRenewalRows(selectedIds);
  };

  const handleRenewalSuccess = (newIdx) => {
      // Fetch the full details of the new request to update context
      getRenewalHistory({ idx: newIdx })
        .then((res) => {
          const newItem = {
            idx: res.idx,
            reqId: `R${String(res.idx).padStart(3, '0')}`,
            reqDate: new Date(res.createdAt).toLocaleDateString(),
            updatedAt: res.updatedAt ? new Date(res.updatedAt).toLocaleDateString() : '-',
            devices: res.deviceCount,
            status: getStatusLabel(res.status),
            statusId: res.status,
            rawDevices: res.devices || []
          };

          // Update Context and Local State
          setSubsRenewals(prev => [newItem, ...prev]);
          setRenewalHistoryData(prev => [newItem, ...prev]);

          // Switch to History tab
          setSearchParams({ ...Object.fromEntries(searchParams), tab: "2" });
          setSelectedDevices([]);
          setPopupModalData(null);

          // Force re-evaluation of device status (since some devices are now "Requested")
          // We can either toggle allDataLoaded off to force fetch,
          // OR better: apply the update locally to allDevs if we want to be fancy.
          // For safety, let's keep the force fetch or relying on the context update for the filter?
          // The filter uses `subsRenewals` context. Since we updated it, the "Renewal: Requested" filter
          // should now correctly include these devices immediately!
          // So we might NOT need to reload all data if we just update the table view.
          // But `ReactTable` rows might need a nudge.
          // setAllDataLoaded(false); // Uncomment if we want to force full refresh
        })
        .catch(err => {
           console.error("Error fetching new request details:", err);
           // Fallback if fetch fails
           setSearchParams({ ...Object.fromEntries(searchParams), tab: "2" });
           setSelectedDevices([]);
           setPopupModalData(null);
           setAllDataLoaded(false);
        });
  };

  const submitRenewal = () => {
    setPopupModalData({
      open: true,
      modalMsg: <Spinner className="renewal-request-loader" size="lg" />,
    });

    const renewalList = devs.filter(d => selectedDevices.includes(d.idx)).map(d => ({
      imei: d.imei,
      regNo: d.reg,
      chassisNo: d.chNum,
      sed: d.sed, // Include subscription end date
      sim: d.sim, // Include SIM number
      // model: d.md, // Include Model
      // make: d.mk, // Include Make
      dlrId: d.dlrId // Include Dealer ID
    }));

    submitRenewalRequest({ devices: renewalList })
      .then((res) => {
        setPopupModalData({
          open: true,
          modalMsg: (
            <div className="renewal-success-icon">
              <UploadingIcon />
              <p>Renewal request submitted for {selectedDevices.length} devices</p>
            </div>
          ),
          confirmBtn: "View Request",
          onConfirm: () => handleRenewalSuccess(res.idx, renewalList),
          onModalClose: () => setPopupModalData(null),
        });
      })
      .catch((err) => {
        console.error("Error submitting renewal:", err);
        setPopupModalData({
          open: true,
          modalMsg: "Error submitting renewal request. Please try again.",
          closeBtn: "Close",
          onModalClose: () => setPopupModalData(null)
        });
      });
  };

  const handleRenewalRequest = () => {
    // If Global Select is active and loading, we must wait for full data to get the list
    if (isGlobalSelectActive && isBackgroundLoading) {
       setLoading(true);
       setPendingRenewal(true);
       return;
    }

    if (selectedDevices.length === 0) return;

    // Calculate correct count for message
    const count = isGlobalSelectActive ? totalDevices : selectedDevices.length;

    setPopupModalData({
      open: true,
      modalMsg: `You are requesting renewal for ${count} devices. Are you sure you want to continue?`,
      closeBtn: "Cancel",
      confirmBtn: "Continue",
      onConfirm: submitRenewal,
      onModalClose: () => setPopupModalData(null),
    });
  };

  const subsDevsDownloadoptions = [
    { label: <div className="bulk-download-options">All devices <DownloadIcon /></div>, downloadAll: true },
    { label: <div className="bulk-download-options">{`Selected devices (${isGlobalSelectActive ? totalDevices : selectedDevices.length})`} <DownloadIcon /></div>, disabled: selectedDevices.length === 0, tooltipText: "Please select at least one device", showTooltip: selectedDevices.length === 0 },
  ];

  const handleSubDevsDownload = (option) => {
    // Block if:
    // 1. "Download All" is clicked AND loading
    // 2. "Download Selected" is clicked AND Global Select is active AND loading (because we need full data to export all)
    if ((option.downloadAll || (isGlobalSelectActive && !option.downloadAll)) && isBackgroundLoading) {
      setLoading(true);
      setPendingDownloadOption(option);
      return;
    }

    const dataToDownload = option.downloadAll ? (allDataLoaded ? allDevs : devs) : devs.filter(d => selectedDevices.includes(d.idx));

    if (dataToDownload.length === 0) {
      toast({
        title: "No Data",
        description: "No devices to download",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const sendToExcel = dataToDownload.map(d => ({
      'Dealership': d.dlr || '-',
      'Registration Number': d.reg || '-',
      'Device IMEI': d.imei || '-',
      // 'SIM Number': d.sim || '-',
      // 'Model': d.md || '-',
      // 'Make': d.mk || '-',
      'Chassis Number': d.chNum || '-',
      'Expiry Date': d.sed ? getLocalisedDate(d.sed, true) : '-',
      'Status': d.status === 1 ? 'Active' : d.status === 2 ? 'Expiring' : d.status === 3 ? 'Expired' : 'SIM Inactive'
    }));

    const args = "Dealership, Registration Number, Device IMEI, Chassis Number, Expiry Date, Status";
    const sendData = sendToExcel.map(exceldata =>
      args.split(",").reduce((mem, arg) => {
        arg = arg.trim();
        mem[arg] = exceldata[arg];
        return mem;
      }, {})
    );

    jsonToCSVConvertor(sendData, "", true, `Device_Subscription_${Date.now()}`, false);
  };


  const getRenewalDropdownOptions = () => [
    { label: "Download All", value: 1 },
    {
      label: `Selected (${selectedRenewalRows.length})`,
      value: 2,
      disabled: selectedRenewalRows.length === 0
    }
  ];

  const handleRenewalDownload = (option) => {
    const dataToDownload = option.value === 1 ? renewalHistoryData : renewalHistoryData.filter(r => selectedRenewalRows.includes(r.idx));

    if (dataToDownload.length === 0) {
      toast({
        title: "No Data",
        description: "No renewal requests to download",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const sendToExcel = dataToDownload.map(r => ({
      'Request ID': r.reqId,
      'Request Date': r.reqDate,
      'Last Updated': r.updatedAt || '-',
      'Number of Devices': r.devices,
      'Status': r.status,
      'Payment Amount': r.paymentDetails?.amount ? `₹${r.paymentDetails.amount}` : '-',
      'Payment Ref No': r.paymentDetails?.refNo || '-',
      'Payment Date': r.paymentDetails?.ts ? new Date(r.paymentDetails.ts).toLocaleDateString() : '-'
    }));

    const args = "Request ID, Request Date, Last Updated, Number of Devices, Status, Payment Amount, Payment Ref No, Payment Date";
    const sendData = sendToExcel.map(exceldata =>
      args.split(",").reduce((mem, arg) => {
        arg = arg.trim();
        mem[arg] = exceldata[arg];
        return mem;
      }, {})
    );

    jsonToCSVConvertor(sendData, "", true, `Renewal_History_${Date.now()}`, false);
  };

  return (
    <div className="device-subscription panel-main">
      {loading && (
        <div className="dev-subs-loading-overlay">
          <Spinner size="xl" thickness="1px" color="var(--ButtonPrimary)" />
        </div>
      )}
      <div className="dev-subs-container">
        <Title>Device Subscription</Title>

        {/* Header */}
        <div className="dev-subs-header">
          <div className="dev-subs-switcher">
            <Switcher
              className="alerts-switcher"
              options={switcherOptions}
              selectedOption={switcherVal}
              handleChange={(o) => setSearchParams({ ...Object.fromEntries(searchParams), tab: o.idx })}
            />
          </div>

          {switcherVal === 1 && (
            <Button
              variant="outline"
              className="upload-renewal-list-btn"
              onClick={() => navigate(`${PATHS.DEVICE_SUBSCRIPTION}${PATHS.UPLOAD_RENEWAL_LIST}`, { state: { dealers: allDealers } })}
            >
              <UploadOutlinedIcon /> Upload Renewal List
            </Button>
          )}
        </div>

        {switcherVal === 1 ? (
          <>
            {/* Status Block */}
            <div className="recent-order-status dev-status">
              <Title>Status ({devStats.total} Devices)</Title>

              <div className="dev-status-cards">
                {devStatuses.map((s, i) => (
                  <div
                    key={i}
                    className={`dev-count-status ${selectedCard === i ? 'card-selected' : ''}`}
                    style={{
                      background: `linear-gradient(to right, ${s.bgStart}, ${s.bgEnd})`,
                      boxShadow: s.shadow
                    }}
                    onClick={() => handleCardClick(i)}
                  >
                    <p className="status-count">{s.count}</p>

                    <div className="status-label-icon-cont">
                      <p className="status-label">{s.label}</p>

                      {s.popover && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <StatusPopover
                            text={s.popover}
                            onOpen={() => setOverlayVisible(true)}
                            onClose={() => setOverlayVisible(false)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Overlay moved outside cards to avoid stacking context issues */}
              {overlayVisible && <div className="status-popover-overlay" />}
            </div>

            <div className="subs-devs-table-container">
              <div className="subs-devs-table-header">
                <Title>Devices ({totalDevices})</Title>
                <div className="subs-download-btns-container">
                  {devs && devs.length ? <OptionList name="Device List" Icon={DownloadIcon} options={subsDevsDownloadoptions} onOptionClick={handleSubDevsDownload} /> : null}
                  {devs && devs.length ?
                    <Button
                      className="req-renewal-btn"
                      onClick={handleRenewalRequest}
                      isDisabled={selectedDevices.length === 0 && !isGlobalSelectActive}
                    >
                      <ResetIcon />
                      Request renewal ({isGlobalSelectActive ? totalDevices : selectedDevices.length})
                    </Button>
                    : null}
                </div>
              </div>

              <div className="subs-filters-row">
                <Input
                  placeholder="Search by Reg no, IMEI, ..."
                  className="subs-search-field"
                  value={searchValue}
                  onChange={handleSearch}
                />

                <AutoComplete
                  placeholder="All Dealerships"
                  options={allDealers}
                  onChange={setDealer}
                  value={dealer}
                  inputCover="select-dealer-in-subs"
                  showSelectedIcon
                />

                {filterConfig.map((fc) => (
                  <OptionList
                    key={fc.key}
                    customClassName="subs-filters-dropdown"
                    name={fc.label}
                    selected={filters[fc.key].label || fc.label}
                    options={fc.options}
                    onOptionClick={(opt) => updateFilter(fc.key, opt)}
                  />
                ))}
              </div>

              <ReactTable
                key={`${JSON.stringify(filters)}-${dealer?.value || ''}-${debouncedSearch}-${allDataLoaded}`}
                data={devs || []}
                columns={columns}
                showSearch={false}
                pageSize={10}
                showSelection={true}
                showSelectAll={true}
                onRowSelect={handleRowSelect}
                autoResetPage={false}
                onPageChange={handlePageChange}
                totalItems={allDataLoaded ? undefined : totalDevices}
                selectedVids={selectedDevices}
              />
            </div>
          </>
        ) : (
          /* Renewal History View */
          <div className="renewal-history-container">
            <div className="renewal-history-header">
              <Title>Renewal History</Title>
              <div className="renewal-history-actions">
                <OptionList
                  name="Download"
                  Icon={DownloadIcon}
                  options={getRenewalDropdownOptions()}
                  onOptionClick={handleRenewalDownload}
                  customClassName="renewal-dropdown"
                />
              </div>
            </div>

            <div className="renewal-history-table-container">
              <ReactTable
                className="renewal-history-table"
                data={renewalHistoryData}
                columns={renewalColumns}
                showSearch={false}
                pageSize={10}
                showSelection={true}
                showSelectAll={true}
                onRowSelect={handleRenewalRowSelect}
                customClassName="renewal-table"
                totalItems={renewalHistoryData.length}
              />
            </div>
          </div>
        )}
      </div>

      <PopUpModal {...popupModalData} />
    </div>
  );
}
